import { useState } from 'react'
import { PageMeta } from '@/components/seo/PageMeta'
import { useAuth } from '@/lib/contexts/auth-context'
import { Container, Heading, Text, Card, Flex, Button, TextField, Avatar, Badge, Tabs, Separator } from '@radix-ui/themes'
import { User, Mail, Calendar, Shield, Bell, Key, Save, Loader2 } from 'lucide-react'

export default function ProfilePage() {
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  })

  const handleSave = async () => {
    setIsLoading(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    setIsLoading(false)
  }

  return (
    <>
      <PageMeta
        title="Profile | Settings"
        description="Manage your profile settings"
      />

      <Container size="3" className="py-8">
        <Flex direction="column" gap="6">
          {/* Header */}
          <Flex direction="column" gap="1">
            <Heading size="7">Profile</Heading>
            <Text color="gray" size="2">
              Manage your personal information and preferences
            </Text>
          </Flex>

          {/* Profile Card */}
          <Card>
            <Flex gap="6" align="start">
              <Avatar
                size="7"
                fallback={user?.firstName?.charAt(0) || 'U'}
                color="blue"
              />
              <Flex direction="column" gap="2" className="flex-1">
                <Heading size="5">{user?.fullName || 'User'}</Heading>
                <Flex gap="4" wrap="wrap">
                  <Flex align="center" gap="2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <Text size="2" color="gray">{user?.email}</Text>
                  </Flex>
                  <Flex align="center" gap="2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <Text size="2" color="gray">
                      Joined {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                    </Text>
                  </Flex>
                </Flex>
                <Flex gap="2" mt="2">
                  {user?.roles?.map((role) => (
                    <Badge key={role.id} variant="soft">
                      <Shield className="h-3 w-3" />
                      {role.name}
                    </Badge>
                  ))}
                  {user?.isEmailVerified && (
                    <Badge color="green" variant="soft">
                      Email Verified
                    </Badge>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Card>

          {/* Tabs */}
          <Tabs.Root defaultValue="personal">
            <Tabs.List>
              <Tabs.Trigger value="personal">
                <User className="h-4 w-4 mr-2" />
                Personal Info
              </Tabs.Trigger>
              <Tabs.Trigger value="security">
                <Key className="h-4 w-4 mr-2" />
                Security
              </Tabs.Trigger>
              <Tabs.Trigger value="notifications">
                <Bell className="h-4 w-4 mr-2" />
                Notifications
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="personal" className="pt-4">
              <Card>
                <Flex direction="column" gap="4">
                  <Heading size="4">Personal Information</Heading>
                  <Separator size="4" />

                  <Flex gap="4">
                    <Flex direction="column" gap="2" className="flex-1">
                      <Text size="2" weight="medium">First Name</Text>
                      <TextField.Root
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        placeholder="First name"
                      />
                    </Flex>
                    <Flex direction="column" gap="2" className="flex-1">
                      <Text size="2" weight="medium">Last Name</Text>
                      <TextField.Root
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        placeholder="Last name"
                      />
                    </Flex>
                  </Flex>

                  <Flex direction="column" gap="2">
                    <Text size="2" weight="medium">Email Address</Text>
                    <TextField.Root
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Email"
                      type="email"
                      disabled
                    />
                    <Text size="1" color="gray">
                      Email cannot be changed. Contact support if you need to update it.
                    </Text>
                  </Flex>

                  <Flex justify="end" pt="4">
                    <Button onClick={handleSave} disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </Flex>
                </Flex>
              </Card>
            </Tabs.Content>

            <Tabs.Content value="security" className="pt-4">
              <Card>
                <Flex direction="column" gap="4">
                  <Heading size="4">Security Settings</Heading>
                  <Separator size="4" />

                  <Flex direction="column" gap="4">
                    <Flex justify="between" align="center">
                      <Flex direction="column" gap="1">
                        <Text weight="medium">Password</Text>
                        <Text size="2" color="gray">
                          Last changed: Never
                        </Text>
                      </Flex>
                      <Button variant="soft">Change Password</Button>
                    </Flex>

                    <Separator size="4" />

                    <Flex justify="between" align="center">
                      <Flex direction="column" gap="1">
                        <Text weight="medium">Two-Factor Authentication</Text>
                        <Text size="2" color="gray">
                          Add an extra layer of security to your account
                        </Text>
                      </Flex>
                      <Button variant="soft">Enable 2FA</Button>
                    </Flex>

                    <Separator size="4" />

                    <Flex justify="between" align="center">
                      <Flex direction="column" gap="1">
                        <Text weight="medium">Active Sessions</Text>
                        <Text size="2" color="gray">
                          Manage devices where you're logged in
                        </Text>
                      </Flex>
                      <Button variant="soft">View Sessions</Button>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            </Tabs.Content>

            <Tabs.Content value="notifications" className="pt-4">
              <Card>
                <Flex direction="column" gap="4">
                  <Heading size="4">Notification Preferences</Heading>
                  <Separator size="4" />

                  <Text color="gray">
                    Notification preferences will be available soon.
                  </Text>
                </Flex>
              </Card>
            </Tabs.Content>
          </Tabs.Root>
        </Flex>
      </Container>
    </>
  )
}
